import { React, useCallback, useEffect, useState, useMemo } from 'react';
import debounce from "lodash.debounce";
import { useTranslation } from "react-i18next";
import { motion } from 'framer-motion';
import "./index.scss";

import SideNav from "../../components/PostLogin/SideNav";
import DomainTileView from '../../components/PostLogin/Domain/DomainTileView';
import DomainListView from '../../components/PostLogin/Domain/DomainListView';
import SideModal from '../../components/ui/SideModal';
import AddDomainModal from '../../components/PostLogin/Domain/AddDomainModal';
import Pagination from '../../components/ui/Pagination';
import NoDataFound from "../../components/ui/NoDataFound";
import { toaster } from "../../components/ui/Toaster";
import SkeletonLoader from "../../components/ui/SkeletonLoader/index.js";

import { tileViewIcon, listViewIcon, searchIcon } from "../../utils/icons.js";
import { formatNumber } from "../../utils/util.js";

import { useSelector, useDispatch } from 'react-redux';
import { getDomainList, resetAddDomainResponse, resetDomainListResponse } from '../../store/actions/organizationActions.js';
import { resetDeleteDomainResponse, getDomainStats, getServerList, resetStartScanResponse } from "../../store/actions/domainAction.js";
import { resetStopScanSingleResponse, resetResumeScanSingleResponse, resetDeleteScanSingleResponse } from "../../store/actions/scanqueueAction.js";

const Domain = () => {

    const domainStatusResponse = useSelector((state) => state.domain?.domainStatusResponse);
    const domainListResponse = useSelector((state) => state.organization?.domainList);
    const addDomainResponse = useSelector((state) => state.organization?.addDomainResponse);
    const deleteDomainResponse = useSelector((state) => state.domain?.deleteDomainResponse);

    const stopScanSingleResponse = useSelector((state) => state.scanQueue?.stopScanSingleResponse);
    const resumeScanSingleResponse = useSelector((state) => state.scanQueue?.resumeScanSingleResponse);
    const deleteScanSingleResponse = useSelector((state) => state.scanQueue?.deleteScanSingleResponse);
    const startScanResponse = useSelector((state) => state.domain?.startScanResponse);

    const { t } = useTranslation();

    const dispatch = useDispatch();

    const [viewType, setViewType] = useState('tile');
    const [showAddOrgSidePanel, setShowAddOrgSidePanel] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [updatedSearchValue, setUpdatedSearchValue] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageSize, setCurrentPageSize] = useState(10);
    const [isToastShown, setIsToastShown] = useState(false);
    const [isStatsLoading, setIsStatsLoading] = useState(false);

    const closeAddOrgSidePanel = () => {
        setShowAddOrgSidePanel(false)
    }

    const searchDomain = (event) => {
        setSearchValue(event.target.value);
        requestSearch(event.target.value)
        setCurrentPage(1);
    }

    const requestSearch = useMemo(() => debounce((searchTerm) => {        
        setUpdatedSearchValue(searchTerm)
    }, 500), []);

    const fetchServerList = useCallback(()=>{
        dispatch(getServerList())
    },[dispatch])

    const fetchDomainList = useCallback(()=>{        
        const baseUrl = `&page=${currentPage}&page_size=${currentPageSize}`;
        const queryParams = updatedSearchValue ? `&query=${updatedSearchValue}` : "";
        const url = `${baseUrl}${queryParams}`
        setIsLoading(true);
        dispatch(getDomainList(url))
    },[updatedSearchValue, currentPage, currentPageSize, dispatch])

    const setCurrentPageandRecordSize = (currentPage, pageSize) => {
        setCurrentPage(currentPage);
        if(pageSize !== currentPageSize) {
            setCurrentPageSize(pageSize);
            setCurrentPage(1);
        }
    }

    const fetchStats = useCallback(()=>{
        setIsStatsLoading(true);
        dispatch(getDomainStats());
    },[dispatch])

    useEffect(() => {
        if(domainListResponse!= null) {
            setIsLoading(false)
        }
    }, [domainListResponse]);

    useEffect(()=>{
        fetchDomainList();        
    },[fetchDomainList])

    useEffect(()=>{
        fetchStats();
    },[fetchStats])

    const closeAddDomain = () => {
        closeAddOrgSidePanel()
        dispatch(resetAddDomainResponse())
        if(addDomainResponse != null) {
            if(addDomainResponse?.detail?.status === "success") {
                fetchDomainList()
                fetchStats();                
            }
        }
    }

    useEffect(()=>{
        if(deleteDomainResponse != null && !isToastShown) {
            if(deleteDomainResponse?.detail?.status === "success") {
                setIsLoading(true);
                fetchDomainList();
                fetchStats();
                dispatch(resetDeleteDomainResponse())
                toaster.addToast(deleteDomainResponse?.detail?.description, "success");
                setIsToastShown(true);
            }
        }
    },[dispatch, deleteDomainResponse, fetchDomainList, isToastShown, fetchStats])

    useEffect(() => {
        if (deleteDomainResponse === null) {
            setIsToastShown(false); // Reset the flag to allow future toasts
        }
    }, [deleteDomainResponse]);

    useEffect(()=>{
        if(stopScanSingleResponse !== null) {
            if(stopScanSingleResponse?.detail?.status === "success") {
                dispatch(resetDomainListResponse())
                toaster.addToast(stopScanSingleResponse?.detail?.description, "success");
                fetchDomainList();
                fetchStats();
            } else {
                toaster.addToast(stopScanSingleResponse?.description, "error");
            }
            dispatch(resetStopScanSingleResponse())
        }
    },[stopScanSingleResponse, fetchDomainList, dispatch, fetchStats])

    useEffect(()=>{
        if(resumeScanSingleResponse !== null) {
            if(resumeScanSingleResponse?.detail?.status === "success") {
                dispatch(resetDomainListResponse())
                toaster.addToast(resumeScanSingleResponse?.detail?.description, "success");
                fetchDomainList();
                fetchStats();
            } else {
                toaster.addToast(resumeScanSingleResponse?.description, "error");
            }
            dispatch(resetResumeScanSingleResponse())
        }
    },[resumeScanSingleResponse, fetchDomainList, dispatch, fetchStats])

    useEffect(()=>{
        if(deleteScanSingleResponse !== null) {
            if(deleteScanSingleResponse?.detail?.status === "success") {
                dispatch(resetDomainListResponse())
                toaster.addToast(deleteScanSingleResponse?.detail?.description, "success");
                fetchDomainList();
                fetchStats();
            } else {
                toaster.addToast(deleteScanSingleResponse?.description, "error");
            }
            dispatch(resetDeleteScanSingleResponse())
        }
    },[deleteScanSingleResponse, fetchDomainList, dispatch, fetchStats])

    useEffect(()=>{
        if(startScanResponse !== null) {
            setIsLoading(false);
            if(startScanResponse?.status === "success") {
                fetchDomainList()
            }
            dispatch(resetStartScanResponse())
        }
    },[startScanResponse, fetchDomainList, dispatch])

    useEffect(()=>{
        if(domainStatusResponse !== null){
            setIsStatsLoading(false);
        }
    },[domainStatusResponse])


    return (
        <div className="domain-container">
            <SideNav />
            <div className="domain-page-container">
                <div className="page-header-container px-4 py-3 d-flex align-items-center justify-content-between">
                    <div className="header-left">
                        <h1>{t("domainPage.heading")}</h1>
                    </div>
                    <div className="header-right">
                        <button onClick={() => { 
                            setShowAddOrgSidePanel(true)
                            fetchServerList()
                        }}><span>&#43;</span>{t("domainPage.addDomain")}</button>
                    </div>
                </div>
                <div className="page-search-header px-4 py-3  d-flex align-items-center justify-content-between">
                    <div className="left-search-container position-relative">
                        <span className="position-absolute search-icon">{searchIcon()}</span>
                        <input type='text' placeholder='Search Domains' className="search-input" value={searchValue} onChange={searchDomain}/>
                    </div>
                    <div className="right-view-option d-flex">
                        <div className={`tile-view-image ${viewType === 'tile' ? 'active' : ''}`} onClick={() => setViewType('tile')}>
                            {tileViewIcon()}
                        </div>
                        <div className={`list-view-image ${viewType === 'list' ? 'active' : ''}`} onClick={() => setViewType('list')}>
                            {listViewIcon()}
                        </div>
                    </div>
                </div>


                <div className="page-main-section p-4">
                    <div className="total-domain-scans d-flex mt-3 rounded">
                        <div className="total-domains-container rounded p-3 margin-right">
                            {
                                isStatsLoading ?
                                <SkeletonLoader type={"line"} count={2} currentPage={"domain"}/>
                                :
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4 }}
                                >
                                    <h4 className="m-0 mt-1">{formatNumber(domainStatusResponse?.domains?.total)}</h4>
                                    <p className='mt-2'>{t("domainPage.totalDomains")}</p>
                                </motion.div>
                            }                            
                        </div>
                        <div className="total-domains-container rounded p-3">
                            {
                                isStatsLoading ?
                                <SkeletonLoader type={"line"} count={2} currentPage={"domain"}/>
                                :
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4 }}
                                >
                                    <div className='domain-icon'>
                                        <h4 className="m-0 mt-1">{formatNumber(domainStatusResponse?.scans?.total)}</h4>
                                        <div className="user-count mx-3 mt-2">
                                            <span className='active-icon d-inline-block'></span>
                                            <span className='passive-icon d-inline-block'></span>
                                            <p className='total-scans-text'>{t("domainPage.totalScans")}</p>
                                        </div>
                                    </div>
                                    <div className="p-wrapper d-flex justify-content-between gap-3">
                                        <p className='mt-2'><span className='count d-inline-block'>{formatNumber(domainStatusResponse?.scans?.active)}</span>{t("domainPage.activeScans")}</p>
                                        <p className='mt-2'><span className='count-passive'>{formatNumber(domainStatusResponse?.scans?.passive)}</span>{t("domainPage.passiveScans")}</p>
                                    </div>
                                </motion.div>
                            }
                        </div>         
                    </div>
                    <h2 className="mb-0">{t("domainPage.pageHeading")}</h2>
                    {viewType === 'tile' && <DomainTileView domainList={domainListResponse} isLoading={isLoading} />}
                    {viewType === 'list' && <DomainListView domainList={domainListResponse} isLoading={isLoading} />}
                    {domainListResponse != null && domainListResponse?.results.length > 0 && !isLoading &&<Pagination
                        className="pagination-bar"
                        currentPage={currentPage}
                        pageSize={currentPageSize}
                        totalCount={domainListResponse.meta.total_results}
                        onPageChange={setCurrentPageandRecordSize}
                    />}
                    {
                        !isLoading && domainListResponse?.meta?.total_results === 0 &&
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4 }}
                        >
                            <NoDataFound />
                        </motion.div>
                    }
                    
                </div>
            </div>
            <SideModal showSidePanel={showAddOrgSidePanel} hideSidePanel={closeAddDomain}>
                {showAddOrgSidePanel && <AddDomainModal closeAddDomain={closeAddDomain}/>}
            </SideModal>
        </div>
    )
}

export default Domain;